import React from "react";
import { createUseStyles, useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { MentionModel } from "../../../models/MentionModel";
import NetPromoterItemRenderer from "./NetPromoterItemRenderer";
import useStyle from "./NetPromoterRenderer.styles";
import useQuestion from "../../../hooks/useQuestion";
import useDimensionContext from "../../../hooks/useDimensionContext";
import useTranslatable from "../../../hooks/useTranslatable";
import OpenEndRenderer from "./OpenEndRenderer";
import { QuestionLabel } from "./QuestionRenderer";

interface Props extends RendererProps {
    mentions: MentionModel[];
    choices: ChoiceModel[];
    onSelect(choice: ChoiceModel): void;
    showFeedbackQuestion?: boolean;
    feedbackQuestion?: string;
    feedbackVariable?: string;
    openEndValue?: string;
    onOpenEndChange?(event: any): void;
    onOpenEndBlur?(): void;
}

function isSelected(props: Props, choice: ChoiceModel) {
    return props.mentions.some((mention) => mention.value === choice.value);
}

export default function NetPromoterRenderer(props: Props) {
    const theme = useTheme();
    const classes = useStyle({ theme });
    const {
        choices,
        gridRendering,
        variable,
        onSelect,
        showFeedbackQuestion,
        feedbackQuestion,
        feedbackVariable,
        openEndValue,
        onOpenEndChange,
        onOpenEndBlur,
    } = props;
    const question = useQuestion();
    const leftLabel = useTranslatable(question, "LeftLabel");
    const rightLabel = useTranslatable(question, "RightLabel");
    const displayQuestion = useTranslatable(question, feedbackQuestion);
    const dimension = useDimensionContext();

    return (
        <div>
            {(leftLabel !== null || rightLabel !== null) && (
                <div
                    className={classnames(
                        classes.label,
                        classes.mobileLabel,
                        dimension && dimension.maxWidth(480) && classes.dynamicMobileLabel,
                    )}
                    dangerouslySetInnerHTML={{ __html: leftLabel }}
                />
            )}
            <div
                className={classnames(
                    classes.choices,
                    dimension && dimension.maxWidth(480) && classes.dynamicChoices,
                )}
            >
                {choices.map((choice) => {
                    const selected = isSelected(props, choice);
                    return (
                        <NetPromoterItemRenderer
                            key={choice.elementId}
                            {...getRendererProps(props)}
                            gridRendering={gridRendering}
                            selected={selected}
                            choice={choice}
                            variable={variable}
                            onSelect={onSelect}
                        />
                    );
                })}
            </div>
            {(leftLabel !== null || rightLabel !== null) && (
                <>
                    <div
                        className={classnames(
                            classes.labels,
                            dimension && dimension.maxWidth(480) && classes.dynamicLabels,
                        )}
                    >
                        <div
                            className={classes.label}
                            dangerouslySetInnerHTML={{ __html: leftLabel }}
                        />
                        <div
                            className={classes.label}
                            dangerouslySetInnerHTML={{ __html: rightLabel }}
                        />
                    </div>
                    <div
                        className={classnames(
                            classes.label,
                            classes.mobileLabel,
                            dimension && dimension.maxWidth(480) && classes.dynamicMobileLabel,
                        )}
                        dangerouslySetInnerHTML={{ __html: rightLabel }}
                    />
                </>
            )}
            {showFeedbackQuestion && (
                <>
                    <QuestionLabel
                        question={question}
                        text={displayQuestion}
                        shortText={undefined}
                        classes={createUseStyles(undefined, undefined)}
                    />
                    <OpenEndRenderer
                        {...getRendererProps(props)}
                        name={feedbackVariable}
                        label={displayQuestion}
                        value={openEndValue}
                        isDisabled={false}
                        onOpenEndBlur={onOpenEndBlur}
                        onOpenEndChange={onOpenEndChange}
                    />
                </>
            )}
        </div>
    );
}
